import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import {
  Loader2, Heart, Plus, ShieldCheck, Users, Coins,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const STATUS_META = {
  planned:     { ar: "مخطّط",     he: "מתוכנן",   en: "Planned",     color: "#06B6D4", bg: "#CFFAFE" },
  in_progress: { ar: "قيد التنفيذ", he: "בביצוע",    en: "In progress", color: "#F59E0B", bg: "#FEF3C7" },
  completed:   { ar: "مكتمل",     he: "הושלם",    en: "Completed",   color: "#10B981", bg: "#D1FAE5" },
  archived:    { ar: "مؤرشف",     he: "מאורכב",   en: "Archived",    color: "#6B7280", bg: "#E5E7EB" },
};

function fmtMoney(n) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n || 0);
}

export default function CommunityPage() {
  const { user } = useAuth();
  const { language } = useI18n();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMunicipality = user && (user.role === "admin" || user.role === "municipality_admin");

  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <MobileShell
      topBar={
        <div className="h-14 flex items-center px-4 gap-2">
          <div className="w-9 h-9 rounded-full bg-[#D1FAE5] flex items-center justify-center">
            <Heart size={17} className="text-[var(--te-success)]" />
          </div>
          <h1 className="font-extrabold text-[20px] text-[var(--te-text)] flex-1">
            {L("مشاريع المجتمع", "פרויקטים קהילתיים", "Community projects")}
          </h1>
          {isMunicipality && (
            <Link
              to="/projects/new"
              data-testid="project-create-btn"
              className="te-btn-primary !py-2 !px-3 text-[13px]"
            >
              <Plus size={14} /> {L("مشروع", "פרויקט", "Project")}
            </Link>
          )}
        </div>
      }
    >
      <div className="px-4 pt-4 pb-6">
        {/* Verified info banner — only shown when not municipality */}
        {!isMunicipality && (
          <div
            className="te-card !bg-[var(--te-primary-soft)] !border-[var(--te-primary)]/20 p-3.5 mb-4 flex items-start gap-2.5 te-fade-up"
            data-testid="projects-info-banner"
          >
            <ShieldCheck size={18} className="text-[var(--te-primary)] shrink-0 mt-0.5" />
            <div className="text-[12px] leading-snug text-[var(--te-text)]" dir="auto">
              {L(
                "هذه المشاريع رسمية من البلدية. يمكنك دعمها ومتابعتها — لكن إنشاء المشاريع متاح لمسؤولي البلدية فقط.",
                "פרויקטים אלה רשמיים מטעם העירייה. תוכל לתמוך ולעקוב — יצירת פרויקטים זמינה רק לעובדי העירייה.",
                "These projects are official municipality initiatives. You can donate and follow, but only municipality staff can create new ones."
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
        ) : projects.length === 0 ? (
          <div className="te-card p-10 text-center te-fade-up" data-testid="projects-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[var(--te-primary-soft)] flex items-center justify-center mb-3">
              <Heart size={22} className="text-[var(--te-primary)]" />
            </div>
            <h3 className="font-extrabold text-[16px] text-[var(--te-text)]">
              {L("لا توجد مشاريع بعد", "אין פרויקטים עדיין", "No projects yet")}
            </h3>
            <p className="text-[13px] text-[var(--te-text-secondary)] mt-2 max-w-xs mx-auto">
              {L(
                "ستظهر هنا المشاريع الرسمية فور إطلاقها من قِبل البلدية.",
                "פרויקטים רשמיים יופיעו כאן ברגע שהעירייה תפרסם אותם.",
                "Official projects will appear here as the municipality launches them."
              )}
            </p>
            {isMunicipality && (
              <Link
                to="/projects/new"
                className="te-btn-primary text-[13px] mt-4 inline-flex"
                data-testid="projects-empty-cta"
              >
                <Plus size={14} /> {L("أنشئ مشروعاً", "צור פרויקט", "Create project")}
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} language={language} L={L} />
            ))}
          </div>
        )}
      </div>
    </MobileShell>
  );
}

function ProjectCard({ project: p, language, L }) {
  const meta = STATUS_META[p.status] || STATUS_META.planned;
  const statusLabel = meta[language] || meta.en;
  const pct = p.goal_amount > 0 ? Math.min(100, Math.round((p.raised_amount / p.goal_amount) * 100)) : 0;

  return (
    <Link
      to={`/projects/${p.id}`}
      data-testid={`project-card-${p.id}`}
      className="block te-card te-card-hover overflow-hidden te-fade-up"
    >
      {p.image_url && (
        <img src={p.image_url} alt="" className="w-full h-40 object-cover" />
      )}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
            style={{ background: meta.bg, color: meta.color }}
            data-testid={`project-status-${p.id}`}
          >
            {statusLabel}
          </span>
          {p.official && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--te-primary)]"
              data-testid={`project-verified-${p.id}`}
              title={L("مشروع بلدي رسمي", "פרויקט עירוני רשמי", "Official municipality project")}
            >
              <ShieldCheck size={13} />
              {L("رسمي", "רשמי", "Verified")}
            </span>
          )}
        </div>
        <h3 className="font-extrabold text-[17px] text-[var(--te-text)] leading-tight" dir="auto">
          {p.title}
        </h3>
        <p className="text-[13px] text-[var(--te-text-secondary)] mt-1 line-clamp-2 leading-relaxed" dir="auto">
          {p.description}
        </p>

        {/* Progress */}
        <div className="mt-3.5">
          <div className="flex items-center justify-between text-[12px] mb-1.5">
            <span className="font-semibold text-[var(--te-text)]" dir="ltr">
              ₪{fmtMoney(p.raised_amount)} <span className="text-[var(--te-text-muted)] font-normal">/ ₪{fmtMoney(p.goal_amount)}</span>
            </span>
            <span className="font-bold text-[var(--te-primary)]">{pct}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-[var(--te-input-bg)] overflow-hidden">
            <div
              className="h-full bg-[var(--te-primary)] transition-all"
              style={{ width: `${pct}%` }}
              data-testid={`project-progress-${p.id}`}
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--te-divider)]">
          <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--te-text-secondary)]">
            <Users size={13} /> {p.supporters_count} {L("داعمون", "תומכים", "supporters")}
          </span>
          <span
            className="te-btn-primary !py-1.5 !px-3 text-[12px]"
            data-testid={`project-donate-cta-${p.id}`}
          >
            <Coins size={13} /> {L("ادعم", "תרום", "Donate")}
          </span>
        </div>
      </div>
    </Link>
  );
}
